import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { usePageTitle } from '../hooks/usePageTitle';
import { formatDate, getUpcomingScheduledSessions } from '../utils/dateUtils';
import { Subscription, TeamMember, Schedule, Course } from '../types';

const MAX_TEAM_STUDENTS = 10;

const TeamSetup: React.FC = () => {
  usePageTitle('Team Setup');
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const { user } = useAuth();
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [inviting, setInviting] = useState<string | null>(null);
  const [enrolling, setEnrolling] = useState<string | null>(null);

  // New member form
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberEmail, setNewMemberEmail] = useState('');

  // Coordinator transfer
  const [transferTarget, setTransferTarget] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [subRes, courseRes, scheduleRes] = await Promise.all([
        authFetch(`/api/subscriptions/${subscriptionId}`),
        fetch('/api/courses'),
        fetch('/api/schedules'),
      ]);

      if (!subRes.ok) {
        setError('Subscription not found or you do not have access.');
        return;
      }
      const sub: Subscription = await subRes.json();

      // Verify user is the coordinator
      if (sub.coordinatorEmail?.toLowerCase() !== user?.email?.toLowerCase()) {
        setError('Only the team coordinator can access this page.');
        return;
      }

      setSubscription(sub);

      if (courseRes.ok) {
        const courseList: Course[] = await courseRes.json();
        setCourses(courseList.filter((c) => c.active));
      }

      if (scheduleRes.ok) {
        const scheduleList: Schedule[] = await scheduleRes.json();
        setSchedules(getUpcomingScheduledSessions(scheduleList));
      }
    } catch (err) {
      console.error('Error fetching team setup data:', err);
      setError('Failed to load team data.');
    } finally {
      setLoading(false);
    }
  }, [user, subscriptionId, authFetch]);

  useEffect(() => {
    if (user?.email && subscriptionId) {
      fetchData();
    }
  }, [user, subscriptionId, fetchData]);

  const teamMembers = subscription?.teamMembers || [];

  const handleAddMember = async () => {
    if (!newMemberName.trim() || !newMemberEmail.trim()) return;
    if (teamMembers.length >= MAX_TEAM_STUDENTS) {
      setSaveMessage(`Maximum of ${MAX_TEAM_STUDENTS} team members reached.`);
      return;
    }

    setSaving(true);
    setSaveMessage(null);
    try {
      const res = await authFetch(`/api/subscriptions/${subscriptionId}/team`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'add',
          member: {
            email: newMemberEmail.trim().toLowerCase(),
            name: newMemberName.trim(),
          },
        }),
      });

      if (res.ok) {
        const updated = await res.json();
        setSubscription(updated);
        setNewMemberName('');
        setNewMemberEmail('');
        setSaveMessage('Team member added.');
      } else {
        const data = await res.json();
        setSaveMessage(data.message || 'Failed to add member.');
      }
    } catch {
      setSaveMessage('An error occurred.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async (email: string) => {
    if (!window.confirm(`Remove this team member? Their enrollments will be cancelled.`)) return;

    setSaving(true);
    setSaveMessage(null);
    try {
      const res = await authFetch(`/api/subscriptions/${subscriptionId}/team`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'remove',
          memberEmail: email,
        }),
      });

      if (res.ok) {
        const updated = await res.json();
        setSubscription(updated);
        setSaveMessage('Team member removed.');
      } else {
        const data = await res.json();
        setSaveMessage(data.message || 'Failed to remove member.');
      }
    } catch {
      setSaveMessage('An error occurred.');
    } finally {
      setSaving(false);
    }
  };

  const handleSendInvite = async (email: string) => {
    setInviting(email);
    try {
      const res = await authFetch(`/api/subscriptions/${subscriptionId}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ memberEmail: email }),
      });

      if (res.ok) {
        const updated = await res.json();
        setSubscription(updated);
        setSaveMessage(`Invite sent to ${email}.`);
      } else {
        const data = await res.json();
        setSaveMessage(data.message || 'Failed to send invite.');
      }
    } catch {
      setSaveMessage('An error occurred sending invite.');
    } finally {
      setInviting(null);
    }
  };

  const handleAssignSession = async (memberEmail: string, memberName: string, scheduleId: string) => {
    setEnrolling(`${memberEmail}-${scheduleId}`);
    setSaveMessage(null);
    try {
      const res = await authFetch('/api/enrollments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scheduleId,
          studentName: memberName,
          studentEmail: memberEmail,
          subscriptionId: subscription!.id,
        }),
      });

      if (res.ok) {
        setSaveMessage(`Session assigned to ${memberName}.`);
        // Refresh seat counts
        const scheduleRes = await fetch('/api/schedules');
        if (scheduleRes.ok) {
          const scheduleList: Schedule[] = await scheduleRes.json();
          setSchedules(getUpcomingScheduledSessions(scheduleList));
        }
      } else {
        const data = await res.json();
        setSaveMessage(data.message || 'Failed to assign session.');
      }
    } catch {
      setSaveMessage('An error occurred.');
    } finally {
      setEnrolling(null);
    }
  };

  const handleTransferCoordinator = async (newCoordinatorEmail: string) => {
    if (!window.confirm(`Transfer coordinator role to ${newCoordinatorEmail}? You will lose management access.`)) return;

    setSaving(true);
    try {
      const res = await authFetch(`/api/subscriptions/${subscriptionId}/coordinator`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newCoordinatorEmail }),
      });

      if (res.ok) {
        setSaveMessage('Coordinator role transferred. Redirecting to dashboard...');
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        const data = await res.json();
        setSaveMessage(data.message || 'Failed to transfer coordinator role.');
      }
    } catch {
      setSaveMessage('An error occurred.');
    } finally {
      setSaving(false);
      setTransferTarget(null);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading team setup...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Access Denied</h2>
        <p>{error}</p>
        <Link to="/dashboard" className="btn btn-primary">Go to Dashboard</Link>
      </div>
    );
  }

  return (
    <div className="team-setup-page">
      <section className="dashboard-header">
        <div className="section-container">
          <h1 className="page-title">Team Setup</h1>
          <p className="page-subtitle">
            You are the Team Coordinator for {subscription?.companyName || 'your team'}.
            Add students, send invites, and assign sessions.
          </p>
        </div>
      </section>

      {/* Status Banner */}
      <section className="section">
        <div className="section-container">
          <div className="dashboard-stats">
            <div className="dashboard-stat-card">
              <span className="dashboard-stat-number">{teamMembers.length}</span>
              <span className="dashboard-stat-label">of {MAX_TEAM_STUDENTS} Seats Filled</span>
            </div>
            <div className="dashboard-stat-card">
              <span className="dashboard-stat-number">
                {teamMembers.filter((m) => m.inviteStatus === 'sent' || m.inviteStatus === 'accepted').length}
              </span>
              <span className="dashboard-stat-label">Invites Sent</span>
            </div>
            <div className="dashboard-stat-card">
              <span className="dashboard-stat-number">{subscription?.rosterStatus || 'pending'}</span>
              <span className="dashboard-stat-label">Roster Status</span>
            </div>
          </div>

          {saveMessage && (
            <div
              className="alert"
              style={{
                marginTop: '1rem',
                padding: '0.75rem 1rem',
                background: saveMessage.includes('Failed') || saveMessage.includes('error') ? '#f8d7da' : '#d4edda',
                border: `1px solid ${saveMessage.includes('Failed') || saveMessage.includes('error') ? '#f5c6cb' : '#c3e6cb'}`,
                borderRadius: '6px',
                color: saveMessage.includes('Failed') || saveMessage.includes('error') ? '#721c24' : '#155724',
              }}
            >
              {saveMessage}
            </div>
          )}
        </div>
      </section>

      {/* Team Roster */}
      <section className="section section-alt">
        <div className="section-container">
          <h2 className="section-title">Team Roster</h2>
          <div className="sessions-table-wrapper">
            <table className="sessions-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Invite Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {teamMembers.map((member) => (
                  <tr key={member.email}>
                    <td>{member.name}</td>
                    <td>{member.email}</td>
                    <td>
                      <span className={`status-badge ${member.teamRole === 'coordinator' ? 'status-active' : 'status-scheduled'}`}>
                        {member.teamRole}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge status-${member.inviteStatus === 'accepted' ? 'active' : member.inviteStatus === 'sent' ? 'scheduled' : 'cancelled'}`}>
                        {member.inviteStatus}
                      </span>
                    </td>
                    <td>
                      {member.teamRole !== 'coordinator' && (
                        <>
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={() => handleSendInvite(member.email)}
                            disabled={inviting === member.email}
                            style={{ marginRight: '0.5rem' }}
                          >
                            {inviting === member.email ? 'Sending...' : member.inviteStatus === 'sent' ? 'Resend Invite' : 'Send Invite'}
                          </button>
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={() => handleRemoveMember(member.email)}
                            disabled={saving}
                            style={{ color: '#dc3545', borderColor: '#dc3545' }}
                          >
                            Remove
                          </button>
                          {transferTarget !== member.email ? (
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={() => setTransferTarget(member.email)}
                              style={{ marginLeft: '0.5rem' }}
                            >
                              Make Coordinator
                            </button>
                          ) : (
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => handleTransferCoordinator(member.email)}
                              style={{ marginLeft: '0.5rem' }}
                            >
                              Confirm Transfer
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Add Member Form */}
          {teamMembers.length < MAX_TEAM_STUDENTS && (
            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem' }}>Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={newMemberName}
                  onChange={(e) => setNewMemberName(e.target.value)}
                  placeholder="Full name"
                  style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem' }}>Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={newMemberEmail}
                  onChange={(e) => setNewMemberEmail(e.target.value)}
                  placeholder="student@company.com"
                  style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                />
              </div>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleAddMember}
                disabled={saving || !newMemberName.trim() || !newMemberEmail.trim()}
              >
                {saving ? 'Adding...' : 'Add Student'}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Session Assignment */}
      <section className="section">
        <div className="section-container">
          <h2 className="section-title">Assign Sessions</h2>
          <p style={{ marginBottom: '1rem', color: '#6b7280' }}>
            Assign your team members to available training sessions.
          </p>

          {teamMembers.filter((m) => m.teamRole !== 'coordinator' || teamMembers.length === 1).map((member) => (
            <div key={member.email} style={{ marginBottom: '2rem' }}>
              <h3 style={{ marginBottom: '0.5rem' }}>{member.name} ({member.email})</h3>
              {courses.map((course) => {
                const courseSchedules = schedules.filter((s) => s.courseId === course.id);
                if (courseSchedules.length === 0) return null;

                return (
                  <div key={course.id} style={{ marginBottom: '1rem', marginLeft: '1rem' }}>
                    <h4 style={{ fontSize: '0.95rem', marginBottom: '0.5rem' }}>{course.title}</h4>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {courseSchedules.map((schedule) => {
                        const seatsLeft = schedule.maxSeats - schedule.currentEnrollment;
                        const enrollKey = `${member.email}-${schedule.id}`;
                        return (
                          <button
                            key={schedule.id}
                            className="btn btn-outline btn-sm"
                            onClick={() => handleAssignSession(member.email, member.name, schedule.id)}
                            disabled={seatsLeft <= 0 || enrolling === enrollKey}
                            style={{ fontSize: '0.8rem' }}
                          >
                            {formatDate(schedule.sessionDate, { month: 'short', day: 'numeric' })}
                            {' '}({seatsLeft} seats)
                            {enrolling === enrollKey && ' ...'}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {/* Also allow coordinator to assign themselves */}
          {teamMembers.length > 1 && (
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ marginBottom: '0.5rem' }}>
                {subscription?.coordinatorName} (You - Coordinator)
              </h3>
              {courses.map((course) => {
                const courseSchedules = schedules.filter((s) => s.courseId === course.id);
                if (courseSchedules.length === 0) return null;

                return (
                  <div key={course.id} style={{ marginBottom: '1rem', marginLeft: '1rem' }}>
                    <h4 style={{ fontSize: '0.95rem', marginBottom: '0.5rem' }}>{course.title}</h4>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {courseSchedules.map((schedule) => {
                        const seatsLeft = schedule.maxSeats - schedule.currentEnrollment;
                        const enrollKey = `${subscription?.coordinatorEmail}-${schedule.id}`;
                        return (
                          <button
                            key={schedule.id}
                            className="btn btn-outline btn-sm"
                            onClick={() => handleAssignSession(
                              subscription!.coordinatorEmail!,
                              subscription!.coordinatorName!,
                              schedule.id
                            )}
                            disabled={seatsLeft <= 0 || enrolling === enrollKey}
                            style={{ fontSize: '0.8rem' }}
                          >
                            {formatDate(schedule.sessionDate, { month: 'short', day: 'numeric' })}
                            {' '}({seatsLeft} seats)
                            {enrolling === enrollKey && ' ...'}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="section">
        <div className="section-container" style={{ textAlign: 'center' }}>
          <Link to="/dashboard" className="btn btn-outline">
            Go to Dashboard
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TeamSetup;
